import { parseISO } from 'date-fns';
import * as React from 'react';
import * as A from 'fp-ts/Array';

import { pipe } from 'fp-ts/lib/function';
import { formatInTimeZone } from 'date-fns-tz';
import { TextLink } from '../../../components/ClickableText';
import { EdgeContainer } from '../../../components/Container';
import { FullMatchFragment } from '../../../generated/graphql';
import { paths } from '../../../paths';
import { dateFormats } from '../../../utils/dateFormats';
import { hasScore } from '../matchUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { config } from '../../../config';
import { getScoreInfo } from '../utils';
import { JoinTeamPrompt } from '../components/JoinTeamPrompt';
import { PayCaptainLink } from '../components/PayCaptainLink';
import { CardHeading } from '../../../components/CardHeading';
import { imageSize } from '../../../utils/imagekit';
import { isNil } from '../../../utils/isNil';

import { IconHandshake } from '../../../icons/Handshake';
import { VoteWinnersCard } from './VoteWinnersCard';
import { CastVotesCard } from './CastVotesCard';
import { MatchStatsCards } from './MatchStatsCards';
import { MatchReportCard } from './MatchReportCard';
import { MatchPhotosCard } from './MatchPhotosCard';
import { PostMatchPrompts } from './PostMatchPrompts';
import { MatchActivitiesCard } from './MatchActivitiesCard';

interface Props {
  match: FullMatchFragment;
}

export const PostMatch: React.FC<Props> = ({ match }) => {
  const playedAt = parseISO(match.playedAt);
  const { user } = useAuth();

  const isSignedIn = !isNil(user);

  const scoreInfo = getScoreInfo(match);
  const isTeamMember = !isNil(match.team.player) || match.team.isCaptain;
  const votingEnabled = pipe(
    match.team.voteTypes,
    A.some((vt) => vt.enabled),
  );
  const votesReadable = match.votingStatus === 'CLOSED' && match.voteCount > 0;

  return (
    <EdgeContainer className="space-y-6 bg-grey-50 px-6 -mb-6 pb-6">
      <div className="-mx-6 px-6 -mt-6 sm:pt-6 bg-split-x-1/3 from-primary-500 to-transparent sm:bg-none sm:bg-transparent">
        <div className="bg-white space-y-6 rounded-lg shadow overflow-hidden">
          {match.image ? (
            <div className="h-48 overflow-hidden">
              <img
                src={imageSize(match.image.url, 'hero')}
                className="object-cover w-full h-full"
                alt={match.image.caption || undefined}
              />
            </div>
          ) : null}
          <div className="p-4">
            <div>
              <dl className="flex justify-between mb-4">
                <div>
                  <dt>
                    <CardHeading>Date</CardHeading>
                  </dt>
                  <dd>
                    <time dateTime={match.playedAt}>
                      {formatInTimeZone(
                        playedAt,
                        match.timeZone,
                        dateFormats.longDate,
                      )}
                    </time>
                  </dd>
                </div>
                <div className="text-right">
                  <dt>
                    <CardHeading>Time</CardHeading>
                  </dt>
                  <dd>
                    {formatInTimeZone(
                      playedAt,
                      match.timeZone,
                      dateFormats.time,
                    )}
                  </dd>
                </div>
              </dl>
              {match.team.isCaptain ? (
                <p className="text-right">
                  <TextLink
                    to={paths.match.settings.home(match.id)}
                    className="text-sm"
                  >
                    Edit match
                  </TextLink>
                </p>
              ) : null}
            </div>
            {hasScore(match) ? (
              <dl className="block">
                <dt className="text-xs uppercase font-semibold">Score</dt>
                <dd className="flex">
                  <div>
                    <div
                      className={`flex justify-between w-full ${
                        (scoreInfo.homeGoals || 0) > (scoreInfo.awayGoals || 0)
                          ? 'font-semibold'
                          : ''
                      }`}
                    >
                      <div className="mr-4">{scoreInfo.homeTeam}</div>{' '}
                      <div>{scoreInfo.homeGoals}</div>
                    </div>

                    <div
                      className={`flex justify-between w-full ${
                        (scoreInfo.awayGoals || 0) > (scoreInfo.homeGoals || 0)
                          ? 'font-semibold'
                          : ''
                      }`}
                    >
                      <div className="mr-4">{scoreInfo.awayTeam}</div>{' '}
                      <div>{scoreInfo.awayGoals}</div>
                    </div>
                  </div>

                  {match.team.isCaptain ? (
                    <TextLink
                      to={paths.match.settings.score(match.id)}
                      className="inline-block ml-auto text-sm self-end"
                    >
                      Edit score
                    </TextLink>
                  ) : null}
                </dd>
              </dl>
            ) : match.team.isCaptain ? (
              <TextLink
                to={paths.match.settings.score(match.id)}
                className="inline-block ml-auto text-sm"
              >
                Add score
              </TextLink>
            ) : null}
            {match.excludedFromStats ? (
              <div
                title="Friendly. This match does not count towards stats and leaderboards"
                className="mt-4 flex justify-end"
              >
                <IconHandshake className="text-grey-600 w-7 h-auto" />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {match.team.isCaptain ? <PostMatchPrompts match={match} /> : null}

      <MatchActivitiesCard match={match} />

      <MatchStatsCards
        matchId={match.id}
        isOwner={match.team.isCaptain}
        isSignedIn={isSignedIn}
      />

      {isTeamMember || !isNil(match.matchReport) || votesReadable ? (
        <h2 className="font-semibold">Match Highlights</h2>
      ) : null}

      {match.votingStatus === 'ACTIVE' && votingEnabled ? (
        <CastVotesCard match={match} />
      ) : null}

      {votesReadable ? (
        <VoteWinnersCard
          match={match}
          isTeamMember={isTeamMember}
          isSignedIn={isSignedIn}
        />
      ) : null}

      {match.matchReport ? <MatchReportCard match={match} /> : null}

      {config.features.matchImages ? <MatchPhotosCard match={match} /> : null}

      {match.team.paymentLink && !match.team.isCaptain ? (
        <PayCaptainLink paymentLink={match.team.paymentLink} />
      ) : null}

      {isNil(match.team.player) && !match.team.isCaptain ? (
        <JoinTeamPrompt team={match.team} />
      ) : null}
    </EdgeContainer>
  );
};
